import React, { useState } from 'react'
import * as Headless from '@headlessui/react'
import { Avatar } from '../catalyst/avatar'
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '../catalyst/dropdown'
import {
  Navbar,
  NavbarDivider,
  NavbarItem,
  NavbarLabel,
  NavbarSection,
  NavbarSpacer,
} from '../catalyst/navbar'
import {
  Sidebar,
  SidebarBody,
  SidebarHeader,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
} from '../catalyst/sidebar'
import { Logo } from './Logo'
import { UserMenu } from './UserMenu'
import { ChevronDownIcon } from '@heroicons/react/16/solid'
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { getNavItems, isNavItemCurrent, publicNavItems, type NavRoles } from '../config/navConfig'
import { useDarkMode } from '../hooks/useDarkMode'
import { LightbulbThemeToggle } from './LightbulbThemeToggle'

function OpenMenuIcon() {
  return (
    <svg data-slot="icon" viewBox="0 0 20 20" aria-hidden="true">
      <path d="M2 6.75C2 6.33579 2.33579 6 2.75 6H17.25C17.6642 6 18 6.33579 18 6.75C18 7.16421 17.6642 7.5 17.25 7.5H2.75C2.33579 7.5 2 7.16421 2 6.75ZM2 13.25C2 12.8358 2.33579 12.5 2.75 12.5H17.25C17.6642 12.5 18 12.8358 18 13.25C18 13.6642 17.6642 14 17.25 14H2.75C2.33579 14 2 13.6642 2 13.25Z" />
    </svg>
  )
}

function CloseMenuIcon() {
  return (
    <svg data-slot="icon" viewBox="0 0 20 20" aria-hidden="true">
      <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
    </svg>
  )
}

function MobileSidebar({
  open,
  close,
  children,
}: React.PropsWithChildren<{ open: boolean; close: () => void }>) {
  return (
    <Headless.Dialog open={open} onClose={close} className="lg:hidden relative z-50">
      <Headless.DialogBackdrop
        transition
        className="fixed inset-0 z-50 bg-[color:var(--theme-overlay)] transition duration-normal ease-out-soft data-closed:opacity-0 data-leave:duration-fast data-leave:ease-in"
      />
      <Headless.DialogPanel
        transition
        className="fixed inset-y-0 z-50 w-full max-w-80 p-2 transition duration-normal ease-out-soft data-closed:-translate-x-full data-leave:duration-fast data-leave:ease-in"
      >
        <div className="theme-surface flex h-full flex-col rounded-lg shadow-xs ring-1 ring-[color:var(--theme-border-soft)]">
          <div className="-mb-3 px-4 pt-3">
            <Headless.CloseButton as={NavbarItem} aria-label="Close navigation">
              <CloseMenuIcon />
            </Headless.CloseButton>
          </div>
          {children}
        </div>
      </Headless.DialogPanel>
    </Headless.Dialog>
  )
}

// The app menu for signed-in members browsing outside the dashboard. Together
// with the header row it reaches everything the dashboard sidebar offers, but it
// is not a copy of the sidebar and should not be kept in sync with it: Dashboard
// sits in the header row instead, and Lodges appears here because public pages
// have no root-admin lodge switcher to reach it through. `navConfig` is what
// decides; check the `teamMenu` surface there before assuming a link is missing.
function TeamDropdownMenu({ roles, lodgeId }: { roles: NavRoles; lodgeId?: number | null }) {
  return (
    <DropdownMenu className="min-w-80 lg:min-w-64" anchor="bottom start">
      {getNavItems('teamMenu', roles, lodgeId).map(({ label, url, icon: Icon }) => (
        <DropdownItem key={label} href={url}>
          {Icon && <Icon />}
          <DropdownLabel>{label}</DropdownLabel>
        </DropdownItem>
      ))}
    </DropdownMenu>
  )
}

function userInitials(email?: string) {
  if (!email) return undefined
  return email
    .split('@')[0]
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase())
    .slice(0, 2)
    .join('')
}

interface HomepageNavProps {
  signedIn: boolean
  currentPath: string
  signInUrl: string
  signOutUrl: string
  /** Null when signed out - there is no member to link to. */
  profileUrl: string | null
  memberCardUrl?: string | null
  avatarUrl?: string | null
  userEmail?: string | null
  authenticityToken: string
  isBoardMember: boolean
  isLodgeAdmin: boolean
  isRootAdmin: boolean
  lodgeId?: number | null
}

export function HomepageNav({
  signedIn,
  currentPath,
  signInUrl,
  signOutUrl,
  profileUrl,
  memberCardUrl,
  avatarUrl,
  userEmail,
  authenticityToken,
  isBoardMember,
  isLodgeAdmin,
  isRootAdmin,
  lodgeId,
}: HomepageNavProps) {
  const [showSidebar, setShowSidebar] = useState(false)
  const [isDark, setDarkMode] = useDarkMode()
  const initials = avatarUrl ? undefined : userInitials(userEmail || undefined)
  const isHomePage = currentPath === '/'
  const roles: NavRoles = { isBoardMember, isLodgeAdmin, isRootAdmin }
  const navItems = signedIn
    ? getNavItems('header', roles, lodgeId).filter(({ url }) => !(isHomePage && url === '/'))
    : isHomePage
      ? []
      : publicNavItems
  // The sidebar always has somewhere to go - app links when signed in, Find a
  // Lodge and Member Sign In when not - so the trigger is always worth showing.
  // Hiding it on the signed-out home page stranded phone users: the header's
  // sign-in link is max-sm:hidden, leaving no route to it under 640px.
  const isCurrent = (url: string) => isNavItemCurrent(url, currentPath, lodgeId)
  const navShellClassName = isHomePage
    ? 'relative isolate border-b border-[color:var(--theme-border-soft)] bg-[color:var(--theme-bg)] transition-colors duration-slow ease-out-soft'
    : 'theme-surface relative isolate transition-colors duration-slow ease-out-soft'

  return (
    <div className={navShellClassName}>
      <MobileSidebar open={showSidebar} close={() => setShowSidebar(false)}>
        <Sidebar>
          <SidebarHeader>
            {signedIn ? (
              <Dropdown>
                <DropdownButton as={SidebarItem} className="lg:mb-2.5">
                  <Avatar
                    initials="LL"
                    className="bg-[color:var(--theme-fg)] text-[color:var(--theme-bg)]"
                  />
                  <SidebarLabel>LodgeLink</SidebarLabel>
                  <ChevronDownIcon />
                </DropdownButton>
                <TeamDropdownMenu roles={roles} lodgeId={lodgeId} />
              </Dropdown>
            ) : (
              <SidebarItem href="/" className="lg:mb-2.5">
                <Logo className="h-7 w-auto fill-[color:var(--theme-fg)]" />
                <SidebarLabel>LodgeLink</SidebarLabel>
              </SidebarItem>
            )}
          </SidebarHeader>
          <SidebarBody>
            {navItems.length > 0 && (
              <SidebarSection>
                {navItems.map(({ label, url }) => (
                  <SidebarItem key={label} href={url} current={isCurrent(url)}>
                    {label}
                  </SidebarItem>
                ))}
              </SidebarSection>
            )}
            {!signedIn && (
              <SidebarSection className="mt-6">
                <SidebarItem href="/find-a-lodge">Find a Lodge</SidebarItem>
                <SidebarItem href={signInUrl}>Member Sign In</SidebarItem>
              </SidebarSection>
            )}
          </SidebarBody>
        </Sidebar>
      </MobileSidebar>

      <header className="flex items-center px-4">
        <div className="py-2.5 lg:hidden">
          <NavbarItem onClick={() => setShowSidebar(true)} aria-label="Open navigation">
            <OpenMenuIcon />
          </NavbarItem>
        </div>
        <div className="min-w-0 flex-1">
          <Navbar>
            <NavbarItem href="/" className="lg:hidden">
              <Logo className="h-6 w-auto fill-[color:var(--theme-fg)]" />
              <NavbarLabel>LodgeLink</NavbarLabel>
            </NavbarItem>
            {signedIn ? (
              <Dropdown>
                <DropdownButton as={NavbarItem} className="max-lg:hidden">
                  <Avatar
                    initials="LL"
                    className="bg-[color:var(--theme-fg)] text-[color:var(--theme-bg)]"
                  />
                  <NavbarLabel>LodgeLink</NavbarLabel>
                  <ChevronDownIcon />
                </DropdownButton>
                <TeamDropdownMenu roles={roles} lodgeId={lodgeId} />
              </Dropdown>
            ) : (
              <NavbarItem href="/" className="max-lg:hidden">
                <Logo className="h-7 w-auto fill-[color:var(--theme-fg)]" />
                <NavbarLabel>LodgeLink</NavbarLabel>
              </NavbarItem>
            )}
            {navItems.length > 0 && (
              <>
                <NavbarDivider className="max-lg:hidden" />
                <NavbarSection className="max-lg:hidden">
                  {navItems.map(({ label, url }) => (
                    <NavbarItem key={label} href={url} current={isCurrent(url)}>
                      {label}
                    </NavbarItem>
                  ))}
                </NavbarSection>
              </>
            )}
            <NavbarSpacer />
            <NavbarSection>
              {signedIn && profileUrl ? (
                // Signed-in members get the theme switch inside the user menu, in
                // the same place the dashboard puts it.
                <Dropdown>
                  <DropdownButton as={NavbarItem}>
                    <Avatar src={avatarUrl || undefined} initials={initials} square />
                  </DropdownButton>
                  <UserMenu
                    profileUrl={profileUrl}
                    memberCardUrl={memberCardUrl}
                    signOutUrl={signOutUrl}
                    authenticityToken={authenticityToken}
                    darkMode={{ isDark, onChange: setDarkMode }}
                  />
                </Dropdown>
              ) : (
                <>
                  <LightbulbThemeToggle toggled={isDark} onToggle={setDarkMode} />
                  <a
                    href="/find-a-lodge"
                    className="theme-button-brand inline-flex min-h-10 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold sm:px-4"
                  >
                    <MagnifyingGlassIcon className="size-4" />
                    <span className="hidden sm:inline">Find a Lodge</span>
                    <span className="sm:hidden">Find</span>
                  </a>
                  <NavbarItem href={signInUrl} className="max-sm:hidden">
                    Member Sign In
                  </NavbarItem>
                </>
              )}
            </NavbarSection>
          </Navbar>
        </div>
      </header>
    </div>
  )
}
